import { NordEvent } from '../common/events.js'

/**
 * The reason a number field value changed, mirroring Base UI's reason union.
 *
 * - `input-change` — a parseable value was typed (or set programmatically as text)
 * - `input-clear` — the field was emptied
 * - `input-blur` — the value was reformatted (and clamped, if enabled) on blur
 * - `input-paste` — a value was pasted
 * - `keyboard` — an Arrow / Page / Home / End key stepped the value
 * - `increment-press` / `decrement-press` — a stepper button changed the value
 * - `wheel` — the mouse wheel scrubbed the value
 * - `scrub` — the scrub area was dragged
 * - `none` — a programmatic / autofill change with no specific reason
 */
export type NumberFieldChangeReason
  = | 'input-change'
    | 'input-clear'
    | 'input-blur'
    | 'input-paste'
    | 'keyboard'
    | 'increment-press'
    | 'decrement-press'
    | 'wheel'
    | 'scrub'
    | 'none'

/**
 * Dispatched when the number field value is committed (on blur, on keyboard
 * step, or on pointer-up after a button hold or scrub drag). Carries the raw
 * numeric value and the reason it changed, mirroring Base UI `onValueCommitted`.
 *
 * The bare event name is `change`, so it composes with native form expectations
 * exactly like the combobox's `change`.
 */
export class NumberFieldChangeEvent extends NordEvent {
  static eventName = 'change'

  constructor(
    /** The committed numeric value, or `null` when the field is empty. */
    public readonly value: number | null,
    /** What triggered the commit. */
    public readonly reason: NumberFieldChangeReason,
  ) {
    super(NumberFieldChangeEvent.eventName)
  }

  /** Convenience accessor mirroring `CustomEvent.detail`. */
  get detail() {
    return this.value
  }
}
